import React, { useState } from "react";
import { Link } from "react-router-dom";
import { InnovativeProdDevItems } from "../pages/InnovativeProdDevItems";
import ContactUsCard from "../ContactUsCard";
import RiBHUS from "../../images/RiBHUS12.png";
import EzeGestImg from "../../images/EzeGest.png";
import DISSOLVRImg from "../../images/DISSOLVR.png";
import P2CImg from "../../images/P2C.png";
import ZEROCRImg from "../../images/ZEROCR.png";
import CapMeltImg from "../../images/CapMelt.png";

const platformTechnologies = [
  {
    id: "ezegest",
    name: "EzeGest™",
    subtitle: "Easy to Swallow",
    tagline: "“Making medicines effortless to take.”",
    features: [
      { text: "Patient-centric technology for ", bold: "smooth ingestion", textAfter: " of desired prescribed dose." },
      { text: "Ideal for ", bold: "paediatric, geriatric, dysphagia-friendly", textAfter: " patients." },
      { text: "Enhances ", bold: "compliance and comfort", textAfter: " without compromising efficacy." }
    ],
    logo: (
      <img src={EzeGestImg} alt="EzeGest™ logo" className="h-auto max-h-[55px] object-contain" />
    )
  },
  {
    id: "dissolvr",
    name: "DISSOLVR™",
    subtitle: "Rapid Dissolution System",
    tagline: "“Speeding science into patient relief.”",
    features: [
      { text: "Engineered for ", bold: "fast solubility", textAfter: " and quick onset of action." },
      { text: "Improves ", bold: "bioavailability", textAfter: " of poorly soluble actives." },
      { text: "Supports ", bold: "quick-acting therapies", textAfter: " for urgent relief." }
    ],
    logo: (
      <img src={DISSOLVRImg} alt="DISSOLVR™ logo" className="h-auto max-h-[55px] object-contain" />
    )
  },
  {
    id: "p2c",
    name: "P2C™",
    subtitle: "Powder to Cream Transformation",
    tagline: "“As patient-friendly formulation with Ease of handling and Compliance.”",
    features: [
      { text: "Converts powders into ", bold: "easy topical application", textAfter: "." },
      { text: "Ensures ", bold: "uniform dosage", textAfter: " across applications." },
      { text: "Adds ", bold: "versatility and extended shelf life", textAfter: "." }
    ],
    logo: (
      <img src={P2CImg} alt="P2C™ logo" className="h-auto max-h-[55px] object-contain" />
    )
  },
  {
    id: "zerocr",
    name: "ZEROCR™",
    subtitle: "Zero Order Controlled Release",
    tagline: "“Consistency you can count on with affordability.”",
    features: [
      { text: "Provides ", bold: "predictable, sustained", textAfter: " drug release." },
      { text: "Eliminates reliance on ", bold: "OROS systems", textAfter: "." },
      { text: "Ideal for ", bold: "long-acting therapies", textAfter: " and compliance-focused regimens." }
    ],
    logo: (
      <img src={ZEROCRImg} alt="ZEROCR™ logo" className="h-auto max-h-[55px] object-contain" />
    )
  },
  {
    id: "capmelt",
    name: "CapMelt™",
    subtitle: "High Dose Formulation",
    tagline: "“Power packed in every capsule.”",
    features: [
      { text: "Delivers ", bold: "high-potency actives", textAfter: " in compact formats." },
      { text: "Ensures ", bold: "efficient absorption", textAfter: " and patient convenience." },
      { text: "Supports ", bold: "specialty pharmaceuticals", textAfter: " with dose flexibility." }
    ],
    logo: (
      <img src={CapMeltImg} alt="CapMelt™ logo" className="h-auto max-h-[55px] object-contain" />
    )
  }
];

export default function InnovativeProdDevRedesign() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [expandedCard, setExpandedCard] = useState("");

  const handleCardToggle = (productId) => {
    if (expandedCard === productId) {
      setExpandedCard("");
    } else {
      setExpandedCard(productId);
    }
  };

  // Filter items based on search and status tabs
  const filteredItems = InnovativeProdDevItems.filter((item) => {
    const matchesSearch =
      item.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.rLDProduct_Dev.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.remark && item.remark.toLowerCase().includes(searchTerm.toLowerCase()));

    const isCompleted = item.licensing === "Done";
    const matchesStatus =
      statusFilter === "All" ||
      (statusFilter === "Completed" && isCompleted) ||
      (statusFilter === "In Progress" && !isCompleted);

    return matchesSearch && matchesStatus;
  });

  // Stepper Node renderer helper
  const renderStepNode = (label, value, isIPStep = false) => {
    let status = "gray"; // completed | active | pending | gray

    if (value === "Done") {
      status = "completed";
    } else if (isIPStep && value !== "Done") {
      status = "active";
    } else if (value === "" || value === "Pending") {
      status = "pending";
    } else {
      status = "gray";
    }

    const configs = {
      completed: {
        bg: "bg-brand-teal text-white",
        border: "border-brand-teal",
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
          </svg>
        ),
        text: "Done"
      },
      active: {
        bg: "bg-brand-blue text-white ring-4 ring-brand-blue/20 animate-pulse",
        border: "border-brand-blue",
        icon: (
          <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 8H17" />
          </svg>
        ),
        text: "Active"
      },
      pending: {
        bg: "bg-amber-50 text-amber-600 border border-amber-300",
        border: "border-amber-300",
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        ),
        text: "Planned"
      },
      gray: {
        bg: "bg-slate-100 text-slate-400 border border-slate-200",
        border: "border-slate-200",
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
          </svg>
        ),
        text: "N/A"
      },
    };

    const current = configs[status];

    return (
      <div className="flex flex-col items-center relative flex-1">
        {/* Node Circle */}
        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold z-10 transition-all ${current.bg}`}>
          {current.icon}
        </div>
        {/* Label */}
        <span className="text-[10px] md:text-xs font-semibold text-slate-500 uppercase tracking-wider mt-1 text-center">
          {label}
        </span>
        {/* Detail status badge */}
        <span className={`text-[9px] font-bold uppercase mt-0.5 px-1.5 py-0.25 rounded-full border ${status === "completed" ? "bg-green-50 text-green-700 border-green-200" :
          status === "active" ? "bg-blue-50 text-blue-700 border-blue-200" :
            status === "pending" ? "bg-amber-50 text-amber-700 border-amber-200" :
              "bg-slate-50 text-slate-400 border-slate-200"
          }`}>
          {current.text}
        </span>
      </div>
    );
  };

  const regionNames = ["USA", "EU", "China", "Australia", "S.E. Asia", "Other"];

  return (
    <div className="bg-[#F7F9FA] min-h-screen text-slate-800">
      {/* 1. Page Hero Section */}
      <section className="relative min-h-[35vh] flex items-center justify-center pt-32 pb-16 overflow-hidden bg-brand-blue-deep">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-25 mix-blend-overlay"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1576086213369-97a306d36557?q=80&w=1920&auto=format&fit=crop')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-blue-deep via-brand-blue/90 to-brand-blue/30" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center text-white z-10">
          <div className="inline-flex items-center space-x-2 bg-brand-teal/20 border border-brand-teal/30 rounded-full px-4 py-1.5 mb-4">
            <span className="text-brand-teal-light font-semibold text-xs uppercase tracking-wider">
              Product Portfolio
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4 leading-tight">
            Innovative Products
          </h1>
          <p className="max-w-3xl mx-auto text-sm md:text-base text-slate-300 font-light leading-relaxed">
            Patient-focused innovation in drug delivery platforms, biosimilars, and prodrugs. We utilize proprietary technologies to extend lifecycle utility and offer patient-centric treatments.
          </p>
        </div>
      </section>

      {/* 2. Affiliated Partner & Tech platform Overview */}
      <section className="py-12 max-w-5xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Affiliate Card */}
        <div className="lg:col-span-5 bg-white border border-slate-100 p-6 md:p-8 rounded-3xl shadow-sm flex flex-col justify-between items-center text-center">
          <div>
            <span className="text-[10px] font-bold text-brand-teal uppercase tracking-widest bg-brand-teal/10 px-2.5 py-1 rounded-full">
              Affiliated R&amp;D Partner
            </span>
            <h3 className="text-lg font-bold text-brand-blue mt-4">RiBHUS Innovation</h3>
            <p className="text-xs text-slate-500 font-light mt-2 leading-relaxed">
              Explore more detailed product parameters and scientific datasets on our R&amp;D partner portal.
            </p>
          </div>
          <div className="mt-6 w-full">
            <a
              href="https://ribhusinnovation.com/Proddev"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center w-full bg-slate-50 hover:bg-slate-100 border border-slate-100 hover:border-brand-teal/30 p-4 rounded-2xl transition-all duration-300 group"
            >
              <img
                src={RiBHUS}
                alt="RiBHUS Innovation"
                className="h-10 object-contain transition-transform group-hover:scale-[1.02]"
              />
            </a>
          </div>
        </div>

        {/* Tech platform description */}
        <div className="lg:col-span-7 bg-white border border-slate-100 p-6 md:p-8 rounded-3xl shadow-sm flex flex-col justify-center space-y-4">
          <span className="inline-block text-xs font-semibold text-brand-teal uppercase tracking-widest">
            Novel Drug Delivery Systems (NDDS)
          </span>
          <h3 className="text-xl md:text-2xl font-bold text-brand-blue tracking-tight leading-tight">
            Extending Lifecycle &amp; Clinical Utility
          </h3>
          <p className="text-slate-600 font-light text-sm leading-relaxed">
            Our Differentiated and Innovative Technology platforms will help our Partners to extend Product Lifecycle and come up with cost effective development. Our Platform Technologies are designed to be Patented, Simple, and Commercially Viable with a strong Patient-Centric approach.
          </p>
        </div>
      </section>

      {/* 2.5 Proprietary Platform Technologies Section */}
      <section className="py-12 bg-slate-50/60 border-y border-slate-200/50">
        <div className="max-w-5xl mx-auto px-6">
          {/* Header */}
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="text-[10px] font-bold text-brand-teal uppercase tracking-widest bg-brand-teal/10 px-3 py-1 rounded-full">
              Proprietary Platform Technologies
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-brand-blue tracking-tight leading-tight mt-4">
              Differentiated Drug Delivery System
            </h2>
            <p className="text-slate-500 font-light mt-3 text-sm leading-relaxed">
              Our advanced delivery platforms are designed to address critical patient compliance, bioavailability, and dosing challenges.
            </p>
          </div>

          {/* Grid Layout: Row 1: 3 cards, Row 2: 2 cards centered */}
          {/* Row 1: EzeGest, DISSOLVR, P2C */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {platformTechnologies.slice(0, 3).map((tech) => (
              <div
                key={tech.id}
                className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 group flex flex-col justify-between"
              >
                {/* Brand / Logo Container */}
                <div className="h-[140px] flex items-center justify-center bg-slate-50/50 border-b border-slate-100 p-6 flex-shrink-0">
                  {tech.logo}
                </div>
                {/* Tech Info */}
                <div className="p-6 flex-grow flex flex-col justify-between">
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-base font-bold text-brand-blue">{tech.name}</h3>
                      <p className="text-[10px] text-brand-teal font-semibold uppercase tracking-wider mt-0.5">{tech.subtitle}</p>
                    </div>
                    {/* Features List */}
                    <ul className="space-y-2 text-xs text-slate-600 font-light leading-relaxed">
                      {tech.features.map((feat, fIdx) => (
                        <li key={fIdx} className="flex items-start">
                          <span className="text-brand-teal mr-1.5 mt-0.5 font-bold">•</span>
                          <span>
                            {feat.text}
                            <strong className="font-semibold text-slate-800">{feat.bold}</strong>
                            {feat.textAfter}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {/* Tagline */}
                  <div className="text-center mt-6 py-2 px-3 bg-slate-50 border border-slate-100/60 rounded-xl">
                    <p className="text-[11px] text-slate-500 font-medium italic leading-snug">
                      {tech.tagline}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Row 2: ZEROCR, CapMelt (Centered & aligned) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 max-w-2xl mx-auto">
            {platformTechnologies.slice(3, 5).map((tech) => (
              <div
                key={tech.id}
                className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 group flex flex-col justify-between"
              >
                {/* Brand / Logo Container */}
                <div className="h-[140px] flex items-center justify-center bg-slate-50/50 border-b border-slate-100 p-6 flex-shrink-0">
                  {tech.logo}
                </div>
                {/* Tech Info */}
                <div className="p-6 flex-grow flex flex-col justify-between">
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-base font-bold text-brand-blue">{tech.name}</h3>
                      <p className="text-[10px] text-brand-teal font-semibold uppercase tracking-wider mt-0.5">{tech.subtitle}</p>
                    </div>
                    {/* Features List */}
                    <ul className="space-y-2 text-xs text-slate-600 font-light leading-relaxed">
                      {tech.features.map((feat, fIdx) => (
                        <li key={fIdx} className="flex items-start">
                          <span className="text-brand-teal mr-1.5 mt-0.5 font-bold">•</span>
                          <span>
                            {feat.text}
                            <strong className="font-semibold text-slate-800">{feat.bold}</strong>
                            {feat.textAfter}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {/* Tagline */}
                  <div className="text-center mt-6 py-2 px-3 bg-slate-50 border border-slate-100/60 rounded-xl">
                    <p className="text-[11px] text-slate-500 font-medium italic leading-snug">
                      {tech.tagline}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2.7 Strategic Impact Section */}
      <section className="py-12 max-w-5xl mx-auto px-6">
        <div className="bg-white border border-slate-100/80 rounded-3xl p-6 md:p-8 shadow-sm relative overflow-hidden">
          {/* Decorative background gradients */}
          <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-bl from-brand-teal/5 to-transparent rounded-tr-3xl -z-10" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-brand-blue/5 to-transparent rounded-bl-3xl -z-10" />

          {/* Heading */}
          <div className="mb-8">
            <span className="text-[10px] font-bold text-brand-teal uppercase tracking-widest bg-brand-teal/10 px-2.5 py-1 rounded-full">
              Strategic Focus
            </span>
            <h3 className="text-xl md:text-2xl font-bold text-brand-blue mt-3">Strategic Impact</h3>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Impact Text Blocks */}
            <div className="lg:col-span-7 space-y-6">
              {/* Card 1: Synergy & NDDS Positioning */}
              <div className="group bg-slate-50 hover:bg-white border border-slate-100 hover:border-brand-teal/30 rounded-2xl p-5 hover:shadow-md transition-all duration-300 flex items-start space-x-4">
                <div className="w-10 h-10 rounded-xl bg-brand-teal/10 text-brand-teal flex items-center justify-center flex-shrink-0 group-hover:bg-brand-teal group-hover:text-white transition-colors duration-300">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                  </svg>
                </div>
                <div className="space-y-1">
                  <h4 className="text-sm font-bold text-brand-blue">Novel Drug Delivery Systems (NDDS)</h4>
                  <p className="text-slate-600 text-xs md:text-sm font-light leading-relaxed">
                    Together, our platform technologies enable patient-friendly and bioavailable formulations that strengthen{" "}
                    <strong className="font-semibold text-slate-800">
                      RiBHUS Innovation’s position in novel drug delivery and specialty pharma.
                    </strong>
                  </p>
                </div>
              </div>

              {/* Card 2: Lifecycle Management */}
              <div className="group bg-slate-50 hover:bg-white border border-slate-100 hover:border-brand-teal/30 rounded-2xl p-5 hover:shadow-md transition-all duration-300 flex items-start space-x-4">
                <div className="w-10 h-10 rounded-xl bg-brand-teal/10 text-brand-teal flex items-center justify-center flex-shrink-0 group-hover:bg-brand-teal group-hover:text-white transition-colors duration-300">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div className="space-y-1">
                  <h4 className="text-sm font-bold text-brand-blue">Lifecycle Enablers</h4>
                  <p className="text-slate-600 text-xs md:text-sm font-light leading-relaxed">
                    These platforms are not just innovations—they’re strategic enablers for lifecycle management, global filings, and commercial success.
                  </p>
                </div>
              </div>
            </div>

            {/* Strategic Quote Card */}
            <div className="lg:col-span-5">
              <div className="relative bg-gradient-to-br from-brand-blue to-brand-blue-deep text-white rounded-2xl p-6 md:p-8 shadow-md overflow-hidden flex flex-col justify-between min-h-[220px]">
                {/* Decorative background quote mark */}
                <div className="absolute right-4 top-top text-white/5 font-serif text-8xl pointer-events-none select-none">
                  “
                </div>
                
                <div className="relative z-10 flex-grow flex items-center">
                  <p className="text-sm md:text-base font-bold italic leading-relaxed text-white/95">
                    “With differentiation and Innovation in Drug Delivery — RiBHUS platforms Technologies turns science into patient-centric advantage.”
                  </p>
                </div>
                
                <div className="mt-6 border-t border-white/10 pt-4 flex items-center justify-between">
                  <span className="text-[10px] uppercase font-bold tracking-widest text-brand-teal-light">
                    Advantage Statement
                  </span>
                  <svg className="w-4 h-4 text-brand-teal-light" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Interactive Search & Filtering Section */}
      <section className="py-6 max-w-5xl mx-auto px-6">
        <div className="bg-white border border-slate-100 p-4 md:p-6 rounded-2xl shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">

          {/* Live Search Input */}
          <div className="relative w-full md:w-72">
            <input
              type="text"
              placeholder="Search product or platform..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 focus:border-brand-teal focus:bg-white rounded-xl pl-10 pr-4 py-2.5 text-sm focus:ring-1 focus:ring-brand-teal/20 transition-all outline-none"
            />
            <svg className="w-4 h-4 text-slate-400 absolute left-3.5 top-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-3 text-slate-400 hover:text-slate-600 text-xs font-bold"
              >
                Clear
              </button>
            )}
          </div>

          {/* Filtering Tab Controls */}
          <div className="flex bg-slate-100 p-1 rounded-xl w-full md:w-auto">
            {["All", "Completed", "In Progress"].map((tab) => (
              <button
                key={tab}
                onClick={() => setStatusFilter(tab)}
                className={`flex-1 md:flex-none px-4 py-2 rounded-lg text-xs font-semibold transition-all ${statusFilter === tab
                  ? "bg-white text-brand-blue shadow-sm"
                  : "text-slate-500 hover:text-brand-blue"
                  }`}
              >
                {tab === "All" ? "All Products" : tab}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Product Cards Grid */}
      <section className="pb-24 max-w-5xl mx-auto px-6">
        <div className="space-y-4">
          {filteredItems.length > 0 ? (
            filteredItems.map((item, idx) => {
              const isExpanded = expandedCard === `prod${item.productID}`;
              return (
                <div
                  key={idx}
                  className={`bg-white border rounded-2xl p-4 md:p-5 shadow-sm hover:shadow-md transition-all duration-300 ${isExpanded
                    ? "border-brand-teal/40 ring-1 ring-brand-teal/20"
                    : "border-slate-100 hover:border-slate-200"
                    }`}
                >

                  {/* Card Header & Stepper Grid */}
                  <div className="space-y-4">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="space-y-1.5 text-left">
                        <div className="flex items-center space-x-2">
                          <h3 className="text-xl font-bold text-brand-blue tracking-tight leading-tight">
                            {item.productName}
                          </h3>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-[10px] font-bold text-brand-teal bg-brand-teal/10 px-2 py-0.5 rounded-full uppercase tracking-wider">
                            Platform: {item.rLDProduct_Dev}
                          </span>
                          <span className="text-xs text-slate-400 font-light">
                            ID: <span className="font-semibold text-slate-500">{item.productID}</span>
                          </span>
                        </div>
                      </div>

                      {/* Expanded trigger button */}
                      <div className="flex items-center space-x-2.5">
                        <button
                          onClick={() => handleCardToggle(`prod${item.productID}`)}
                          className={`inline-flex items-center space-x-2 text-xs font-semibold px-4 py-2 rounded-xl border transition-all ${isExpanded
                            ? "bg-slate-100 text-slate-800 border-slate-200"
                            : "bg-white text-brand-teal hover:text-brand-teal-dark border-slate-200 hover:border-brand-teal/30 hover:bg-slate-50"
                            }`}
                        >
                          <span>{isExpanded ? "Hide Regions" : "View Regions"}</span>
                          <svg
                            className={`w-3 h-3 transform transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Stepper Track */}
                    <div className="relative pt-3 pb-1 border-t border-b border-slate-50">
                      {/* Desktop connecting line */}
                      <div className="hidden md:block absolute left-[10%] right-[10%] top-[28px] h-[2px] bg-slate-200 -z-0" />

                       <div className="grid grid-cols-3 sm:grid-cols-5 md:flex md:flex-row gap-y-6 gap-x-2 md:gap-0 justify-between items-stretch md:items-center relative z-10">
                        {renderStepNode("IP Filed", item.productDevStatus, true)}
                        {renderStepNode("POC", item.ipStatus)}
                        {renderStepNode("Scale Up", item.labStability)}
                        {renderStepNode("In-Vivo Study", item.poc)}
                        {renderStepNode("Tech. Transfer", item.licensing)}
                      </div>
                    </div>
                  </div>

                  {/* Expandable Section: Licensing Table and Action */}
                  {isExpanded && (
                    <div className="mt-6 pt-6 border-t border-slate-100 space-y-6 animate-fadeIn">

                      {/* Remarks Callout */}
                      {item.remark && (
                        <div className="bg-brand-blue/5 border-l-4 border-brand-blue p-4 rounded-r-xl text-left">
                          <p className="text-xs font-bold text-brand-blue uppercase tracking-wider">Scientific Remarks</p>
                          <p className="text-sm text-slate-600 font-light mt-0.5 leading-relaxed italic">
                            "{item.remark}"
                          </p>
                        </div>
                      )}

                      {/* Out-licensing status grid */}
                      <div className="space-y-3 text-left">
                        <h4 className="text-xs font-bold text-brand-blue uppercase tracking-wider">
                          Out-Licensing Availability Status
                        </h4>

                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                          {regionNames.map((regionName, regionIdx) => {
                            const isAvailable = item.avaliableIn && item.avaliableIn[regionIdx] ? item.avaliableIn[regionIdx].value : false;
                            return (
                              <div
                                key={regionIdx}
                                className={`border p-3 rounded-xl flex flex-col justify-between space-y-1 transition-all ${isAvailable
                                  ? "bg-emerald-50/30 border-emerald-100 hover:border-emerald-200"
                                  : "bg-slate-50/50 border-slate-200/60"
                                  }`}
                              >
                                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{regionName}</span>
                                <div className="flex items-center space-x-1.5 mt-1">
                                  <span className={`w-1.5 h-1.5 rounded-full ${isAvailable ? "bg-emerald-500 animate-pulse" : "bg-slate-300"}`} />
                                  <span className={`text-[10px] font-bold uppercase ${isAvailable ? "text-emerald-700" : "text-slate-400"
                                    }`}>
                                    {isAvailable ? "Available" : "Licensed"}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Footer Licensing Apply Row */}
                      <div className="border-t border-slate-100 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                        <p className="text-xs text-slate-400 font-light text-left">
                          If you are interested in commercializing or licensing this innovative product in any of the available regions, contact our Business Development desk.
                        </p>

                        <Link
                          to="/ContactUs"
                          state={{ from: `Licensing Inquiry: ${item.productName}` }}
                          className="w-full sm:w-auto inline-flex items-center justify-center space-x-2 bg-brand-teal hover:bg-brand-teal-dark text-white text-xs font-bold px-5 py-2.5 rounded-xl shadow-sm transition-all transform hover:-translate-y-0.5"
                        >
                          <span>Reach For Licensing</span>
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                        </Link>
                      </div>

                    </div>
                  )}

                </div>
              );
            })
          ) : (
            <div className="text-center bg-white border border-slate-100 rounded-3xl p-16">
              <p className="text-slate-500 font-light">No products match your search or filter criteria.</p>
            </div>
          )}
        </div>
      </section>

      {/* 5. Global Contact Us Card Accent */}
      <ContactUsCard sender="Genr" />
    </div>
  );
}
